// Dashboard Stats Cards Component

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardStats, useSavingsMetrics } from '../../stores/dashboardStore';
import { useWorkshopStore } from '../../stores/workshopStore';
import { DashboardService } from '../../services/dashboard';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  statusText?: string; // New prop for status text under title
  trend?: {
    value: string;
    isPositive: boolean;
  };
  color: 'blue' | 'orange' | 'green' | 'purple' | 'red';
  icon: React.ReactNode;
  compact?: boolean;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, statusText, trend, color, icon, compact = false, onClick }) => {
  const colorClasses = {
    blue: 'bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 border border-blue-200',
    orange: 'bg-gradient-to-br from-orange-50 via-orange-100 to-orange-200 border border-orange-200',
    green: 'bg-gradient-to-br from-green-50 via-green-100 to-green-200 border border-green-200',
    purple: 'bg-gradient-to-br from-purple-50 via-purple-100 to-purple-200 border border-purple-200',
    red: 'bg-gradient-to-br from-red-50 via-red-100 to-red-200 border border-red-200'
  };

  const iconColorClasses = {
    blue: 'text-blue-700 bg-gradient-to-br from-blue-400 to-blue-600',
    orange: 'text-orange-700 bg-gradient-to-br from-orange-400 to-orange-600',
    green: 'text-green-700 bg-gradient-to-br from-green-400 to-green-600',
    purple: 'text-purple-700 bg-gradient-to-br from-purple-400 to-purple-600',
    red: 'text-red-700 bg-gradient-to-br from-red-400 to-red-600'
  };

  if (compact) {
    return (
      <div 
        className={`relative overflow-hidden rounded-lg shadow-sm ${colorClasses[color]} p-2 transition-all duration-200 hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${iconColorClasses[color]} text-white flex-shrink-0`}>
            <div className="w-3 h-3">{icon}</div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">{value}</p>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide leading-tight truncate">{title}</p>
            {statusText && (
              <p className="text-xs text-gray-500 leading-tight truncate">{statusText}</p>
            )}
            {trend && (
              <span className={`text-xs font-medium px-1 py-0.5 rounded inline-block mt-0.5 ${
                trend.isPositive 
                  ? 'text-green-700 bg-green-100/80' 
                  : 'text-red-700 bg-red-100/80'
              }`}>
                {trend.value}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl ${colorClasses[color]} p-6 transform hover:-translate-y-2 hover:rotate-1 transition-all duration-500 ${onClick ? 'cursor-pointer' : ''} group backdrop-blur-sm`}
      onClick={onClick}
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
        <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-white/30 group-hover:scale-150 transition-transform duration-700"></div>
        <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/20 group-hover:scale-125 transition-transform duration-700 delay-100"></div>
      </div>
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-xl ${iconColorClasses[color]} text-white shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300`}>
            {icon}
          </div>
          <div className="text-right">
            <p className="text-3xl font-black text-gray-800 group-hover:scale-110 transition-transform duration-300 tracking-tight">{value}</p>
          </div>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm font-bold text-gray-700 uppercase tracking-wider group-hover:text-gray-800 transition-colors duration-200">{title}</p>
          
          {statusText && (
            <p className="text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-200">{statusText}</p>
          )}
          
          {subtitle && (
            <p className="text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-200 font-medium">{subtitle}</p>
          )}
          
          {trend && (
            <div className="flex items-center justify-between pt-2 border-t border-white/30">
              <span className={`text-xs font-bold px-2 py-1 rounded-full transition-all duration-200 ${
                trend.isPositive 
                  ? 'text-green-700 bg-green-100/80 group-hover:bg-green-200/80' 
                  : 'text-red-700 bg-red-100/80 group-hover:bg-red-200/80'
              }`}>
                {trend.value}
              </span>
              <span className="text-xs text-gray-500 group-hover:text-gray-600 transition-colors duration-200">vs target</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface StatsCardsProps {
  compact?: boolean;
}

const StatsCards: React.FC<StatsCardsProps> = ({ compact = false }) => {
  const { stats, isLoading, error } = useDashboardStats();
  const { savingsMetrics } = useSavingsMetrics();
  const navigate = useNavigate();
  const { setFilters, fetchWorkshops, clearFilters } = useWorkshopStore();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gray-200 rounded-lg mr-4"></div>
              <div className="flex-1">
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-800">Failed to load dashboard statistics: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats || !savingsMetrics) {
    return null;
  }

  // Calculate active workshops (ongoing)
  const activeWorkshops = stats.overview.ongoing_workshops;
  
  // Calculate late workshops (overdue)
  const lateWorkshops = stats.timeline.overdue_workshops;
  
  // Calculate passive workshops
  const passiveWorkshops = stats.overview.passive_workshops;
  
  // Calculate completed workshops
  const completedWorkshops = stats.overview.completed_workshops;
  
  // Get individual status counts
  const statusCounts = stats.overview.status_counts;
  
  // Build status text with counts
  const activeStatusText = [
    statusCounts.new > 0 ? `New (${statusCounts.new})` : null,
    statusCounts.in_progress > 0 ? `In Progress (${statusCounts.in_progress})` : null
  ].filter(Boolean).join(', ');
  
  const passiveStatusText = [
    statusCounts.on_hold > 0 ? `On Hold (${statusCounts.on_hold})` : null,
    statusCounts.risk_of_delay > 0 ? `Risk of Delay (${statusCounts.risk_of_delay})` : null,
    statusCounts.rejected > 0 ? `Rejected (${statusCounts.rejected})` : null
  ].filter(Boolean).join(', ');
  
  const completedStatusText = [
    statusCounts.closed > 0 ? `Closed (${statusCounts.closed})` : null,
    statusCounts.fully_implemented > 0 ? `Fully Implemented (${statusCounts.fully_implemented})` : null
  ].filter(Boolean).join(', ');
  
  // Calculate realization rate
  const realizationRate = savingsMetrics.realization_rate;

  // Navigation handlers for each card type
  // These now preserve the startup date filter from localStorage by NOT calling clearFilters()
  const handleActiveWorkshopsClick = async () => {
    // Set active workshops filter while preserving date filters
    setFilters({ statuses: ['new', 'in_progress'], status: undefined, overdue: undefined });
    await fetchWorkshops();
    navigate('/workshops');
  };

  const handleLateWorkshopsClick = async () => {
    // Set overdue filter while preserving date filters
    setFilters({ overdue: true, statuses: undefined, status: undefined });
    await fetchWorkshops();
    navigate('/workshops');
  };

  const handlePassiveWorkshopsClick = async () => {
    // Set passive workshops filter while preserving date filters
    setFilters({ statuses: ['on_hold', 'risk_of_delay', 'rejected'], status: undefined, overdue: undefined });
    await fetchWorkshops();
    navigate('/workshops');
  };

  const handleCompletedWorkshopsClick = async () => {
    // Set completed workshops filter while preserving date filters
    setFilters({ statuses: ['closed', 'fully_implemented'], status: undefined, overdue: undefined });
    await fetchWorkshops();
    navigate('/workshops');
  };

  if (compact) {
    // All-in-one view: All 7 cards in one row
    return (
      <div className="grid grid-cols-7 gap-1">
        {/* Active Workshops */}
        <StatCard
          title="ACTIVE WORKSHOPS"
          value={activeWorkshops}
          statusText={activeStatusText}
          subtitle="+3 this month"
          trend={{
            value: "+12%",
            isPositive: true
          }}
          color="blue"
          compact={compact}
          onClick={handleActiveWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />

        {/* Late Workshops */}
        <StatCard
          title="LATE WORKSHOPS"
          value={lateWorkshops}
          statusText="Overdue workshops"
          subtitle="Need attention"
          trend={{
            value: "-2 vs last month",
            isPositive: false
          }}
          color="red"
          compact={compact}
          onClick={handleLateWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Passive Workshops */}
        <StatCard
          title="PASSIVE WORKSHOPS"
          value={passiveWorkshops}
          statusText={passiveStatusText}
          subtitle="+1 this month"
          trend={{
            value: "+5%",
            isPositive: false
          }}
          color="orange"
          compact={compact}
          onClick={handlePassiveWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Completed Workshops */}
        <StatCard
          title="COMPLETED WORKSHOPS"
          value={completedWorkshops}
          statusText={completedStatusText}
          subtitle="+5 this month"
          trend={{
            value: "+18%",
            isPositive: true
          }}
          color="green"
          compact={compact}
          onClick={handleCompletedWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Estimated Savings */}
        <StatCard
          title="ESTIMATED SAVINGS"
          value={savingsMetrics.formatted_estimated}
          subtitle="+247K€ vs Q1"
          trend={{
            value: "+15%",
            isPositive: true
          }}
          color="orange"
          compact={compact}
          onClick={handlePassiveWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          }
        />

        {/* Released Savings */}
        <StatCard
          title="RELEASED SAVINGS"
          value={savingsMetrics.formatted_released}
          subtitle="+189K€ vs Q1"
          trend={{
            value: "+18%",
            isPositive: true
          }}
          color="purple"
          compact={compact}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />

        {/* Realization Rate */}
        <StatCard
          title="REALIZATION RATE"
          value={`${realizationRate.toFixed(1)}%`}
          subtitle="+2.3% vs target"
          trend={{
            value: "+2.3%",
            isPositive: true
          }}
          color="green"
          compact={compact}
          onClick={handleCompletedWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 712-2h2a2 2 0 712 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      </div>
    );
  }

  // Normal view: 2 rows - 4 workshop cards + 3 financial cards
  return (
    <div className="space-y-6">
      {/* First Row - Workshop Status Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Active Workshops */}
        <StatCard
          title="ACTIVE WORKSHOPS"
          value={activeWorkshops}
          statusText={activeStatusText}
          subtitle="+3 this month"
          trend={{
            value: "+12%",
            isPositive: true
          }}
          color="blue"
          compact={compact}
          onClick={handleActiveWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />

        {/* Late Workshops */}
        <StatCard
          title="LATE WORKSHOPS"
          value={lateWorkshops}
          statusText="Overdue workshops"
          subtitle="Need attention"
          trend={{
            value: "-2 vs last month",
            isPositive: false
          }}
          color="red"
          compact={compact}
          onClick={handleLateWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Passive Workshops */}
        <StatCard
          title="PASSIVE WORKSHOPS"
          value={passiveWorkshops}
          statusText={passiveStatusText}
          subtitle="+1 this month"
          trend={{
            value: "+5%",
            isPositive: false
          }}
          color="orange"
          compact={compact}
          onClick={handlePassiveWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Completed Workshops */}
        <StatCard
          title="COMPLETED WORKSHOPS"
          value={completedWorkshops}
          statusText={completedStatusText}
          subtitle="+5 this month"
          trend={{
            value: "+18%",
            isPositive: true
          }}
          color="green"
          compact={compact}
          onClick={handleCompletedWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Second Row - Financial Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Estimated Savings */}
        <StatCard
          title="ESTIMATED SAVINGS"
          value={savingsMetrics.formatted_estimated}
          subtitle="+247K€ vs Q1"
          trend={{
            value: "+15%",
            isPositive: true
          }}
          color="orange"
          compact={compact}
          onClick={handlePassiveWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          }
        />

        {/* Released Savings */}
        <StatCard
          title="RELEASED SAVINGS"
          value={savingsMetrics.formatted_released}
          subtitle="+189K€ vs Q1"
          trend={{
            value: "+18%",
            isPositive: true
          }}
          color="purple"
          compact={compact}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />

        {/* Realization Rate */}
        <StatCard
          title="REALIZATION RATE"
          value={`${realizationRate.toFixed(1)}%`}
          subtitle="+2.3% vs target"
          trend={{
            value: "+2.3%",
            isPositive: true
          }}
          color="green"
          compact={compact}
          onClick={handleCompletedWorkshopsClick}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      </div>
    </div>
  );
};

export default StatsCards;